import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Typography, Button, TextField, Container, Box, Link, Grid } from '@mui/material';
import { setAuthCookie, setAuthUser } from '../context/auth';
import client from '../modules/axios';
import { grey } from '@mui/material/colors';
import { useFormik } from 'formik';
import loginSchema from '../modules/validation-schema/loginSchema';
import { API, PATH } from '../config/path';
import { useApp } from '../context/appContext';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as ReactRouterLink } from 'react-router-dom';

const Login = () => {
  const { snackbar, setSnackbar } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isDisabled, setIsDisabled] = useState(false);
  const [from, setFrom] = useState(null);

  useEffect(() => {
    if (searchParams.has('from')) {
      setFrom(decodeURIComponent(searchParams.get('from')));
    }
  }, [searchParams]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setIsDisabled(true);

      try {
        const res = await client.post(API.LOGIN, values);

        const data = res.data.success;

        setAuthCookie(data.token);

        setAuthUser({
          user_id: data.user_id,
          name: data.name,
        });

        setSnackbar({
          ...snackbar,
          open: true,
          message: 'Login successfully',
          type: 'success',
        });

        if (from) return (window.location.href = `${from}?from=login#add-comment`);

        navigate(PATH.HOME);
      } catch (err) {
        if (err.response.status === 500) {
          setSnackbar({
            ...snackbar,
            open: true,
            message: 'Internal server error',
            type: 'error',
          });

          setIsDisabled(false);
          return;
        }

        setSnackbar({
          ...snackbar,
          open: true,
          message: 'Email or password is incorrect',
          type: 'error',
        });

        setIsDisabled(false);
      }
    },
  });

  // console.log('from', from);

  return (
    <>
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 3,
            border: '1px solid' + grey[300],
            borderRadius: 1,
          }}>
          <Box
            sx={{
              backgroundColor: grey[100],
              py: 1.5,
              px: 2.5,
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}>
              <Link
                component={ReactRouterLink}
                to={PATH.HOME}
                color={'primary'}
                underline="hover"
                align="right">
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    py: 0,
                  }}>
                  Home
                </Button>
              </Link>
              <Typography variant="body1">Login</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              py: 2,
              px: 2.5,
            }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1">Email Address</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    variant="outlined"
                    fullWidth
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1">Password</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    variant="outlined"
                    fullWidth
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}></Grid>
                <Grid item xs={12} sm={8}>
                  <Box
                    sx={{
                      pt: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isDisabled}
                      loading={isDisabled}
                      loadingIndicator="Loading…"
                      sx={{
                        px: 4,
                      }}>
                      Login
                    </LoadingButton>

                    <Link
                      component={ReactRouterLink}
                      to={PATH.FORGOT_PASSWORD}
                      color={'primary'}
                      underline="hover">
                      <Typography variant="body1">Forgot Your Password?</Typography>
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Login;
